'use client';

import { UI } from '@/features/marketing/components/_styles';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { NAV_LINKS, NAV_ACTIONS, NAV_LOGO } from '@/features/marketing/data/navigation.data';
import { SITE_NAME } from '@/shared/lib/config';
import { useAuth } from '@/features/auth/auth.hooks';

// `overlay`: the page starts with a dark hero, so at the top the bar floats transparent in light colours with no spacer.
export function EchoNavbar({ overlay = false }: { overlay?: boolean } = {}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthorized, logout } = useAuth();
  const light = overlay && !isScrolled && !mobileMenuOpen;
  const navLink = `${UI.navLink} ${light ? '!text-white' : ''}`;
  const cta = `${UI.btnSm} shadow-sm ${light ? 'bg-white text-[#181a1a] hover:bg-neutral-200' : UI.dark}`;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Static spacer to prevent layout shift */}
      {!overlay && <div className="h-16 w-full shrink-0" aria-hidden="true" />}

      {/* Floating / Retracting Glass Navbar */}
      <div className="fixed inset-x-0 top-0 z-50 pointer-events-none flex justify-center px-4 sm:px-6">
        <motion.nav
          initial={false}
          animate={{
            y: isScrolled ? 14 : 0,
            maxWidth: isScrolled ? 760 : 1200,
            borderRadius: 0,
            backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.35)' : 'rgba(255, 255, 255, 0)',
            backdropFilter: isScrolled ? 'blur(16px)' : 'blur(0px)',
            boxShadow: isScrolled
              ? '0 10px 30px -5px rgba(0, 0, 0, 0.06)'
              : '0 0 0 0 rgba(0, 0, 0, 0)',
            paddingTop: isScrolled ? 8 : 12,
            paddingBottom: isScrolled ? 8 : 12,
          }}
          transition={{
            duration: isScrolled ? 0.55 : 0.2,
            ease: isScrolled ? [0.16, 1, 0.3, 1] : 'easeOut',
            boxShadow: { duration: isScrolled ? 0.35 : 0.04 },
            backgroundColor: { duration: isScrolled ? 0.35 : 0.04 },
            backdropFilter: { duration: isScrolled ? 0.35 : 0.04 },
          }}
          className="pointer-events-auto w-full border-none flex flex-col px-4 sm:px-6 will-change-transform overflow-hidden"
        >
          <div className="flex items-center justify-between gap-4 sm:gap-8 h-9 sm:h-10">
            {/* Brand Logo */}
            <Link href="/" className="flex items-center shrink-0 no-underline hover:opacity-70 transition-opacity" aria-label={`${SITE_NAME} Home`}>
              <Image
                src={NAV_LOGO}
                alt={SITE_NAME}
                width={29}
                height={14}
                className={`h-3.5 w-auto object-contain ${light ? 'brightness-0 invert' : ''}`}
                priority
              />
            </Link>

            {/* Center Navigation Links */}
            <div className="hidden md:flex items-center gap-6 sm:gap-7">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={navLink}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right Action Buttons */}
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              {isAuthorized ? (
                <>
                  <button
                    type="button"
                    onClick={() => logout()}
                    className={`${navLink} px-2 py-1 cursor-pointer`}
                  >
                    {NAV_ACTIONS.logout.label}
                  </button>
                  <Link
                    href={NAV_ACTIONS.dashboard.href}
                    className={cta}
                  >
                    {NAV_ACTIONS.dashboard.label}
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    href={NAV_ACTIONS.login.href}
                    className={`${navLink} px-2 py-1`}
                  >
                    {NAV_ACTIONS.login.label}
                  </Link>
                  <Link
                    href={NAV_ACTIONS.signup.href}
                    className={cta}
                  >
                    {NAV_ACTIONS.signup.label}
                  </Link>
                </>
              )}

              {/* Mobile hamburger button */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden p-1.5 ${light ? 'text-white' : 'text-[#232323]'}`}
                aria-label="Toggle navigation menu"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {mobileMenuOpen ? (
                    <path d="M18 6L6 18M6 6l12 12" />
                  ) : (
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Drawer */}
          {mobileMenuOpen && (
            <div className="md:hidden pt-3 pb-2 mt-2 border-t border-[#edeceb]/60 flex flex-col gap-2">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${UI.navLink} py-1`}
                >
                  {link.label}
                </Link>
              ))}

              <div className="pt-2 mt-1 border-t border-[#edeceb]/60 flex items-center gap-2">
                {isAuthorized ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        logout();
                      }}
                      className={`${UI.navLink} px-3 py-1.5 cursor-pointer`}
                    >
                      {NAV_ACTIONS.logout.label}
                    </button>
                    <Link
                      href={NAV_ACTIONS.dashboard.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex-1 ${UI.btn} ${UI.dark}`}
                    >
                      {NAV_ACTIONS.dashboard.label}
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      href={NAV_ACTIONS.login.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`${UI.navLink} px-3 py-1.5`}
                    >
                      {NAV_ACTIONS.login.label}
                    </Link>
                    <Link
                      href={NAV_ACTIONS.signup.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex-1 ${UI.btn} ${UI.dark}`}
                    >
                      {NAV_ACTIONS.signup.label}
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </motion.nav>
      </div>
    </>
  );
}
